import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Message } from '../../types/chat.types';
import {
  createSessionThunk,
  sendMessageThunk,
  submitFeedbackThunk,
  deleteSessionThunk,
} from '../thunks/sessionThunks';

export interface SessionMeta {
  id: string;
  agentId: string;
  createdAt: string;
  title?: string;
}

export interface SessionState {
  activeSessionId: string | null;
  sessions: Record<string, SessionMeta>;
  messagesBySession: Record<string, Message[]>;
  isLoading: boolean;
  error: string | null;
}

const initialState: SessionState = {
  activeSessionId: null,
  sessions: {},
  messagesBySession: {},
  isLoading: false,
  error: null,
};

/** Get messages for the active session (helper) */
function getActiveMessages(state: SessionState): Message[] {
  if (!state.activeSessionId) return [];
  return state.messagesBySession[state.activeSessionId] ?? [];
}

/** Set messages for the active session (helper) */
function setActiveMessages(state: SessionState, messages: Message[]): void {
  if (state.activeSessionId) {
    state.messagesBySession[state.activeSessionId] = messages;
  }
}

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    /** Clear all session state (e.g., when switching agents) */
    clearSessions: (state) => {
      state.activeSessionId = null;
      state.sessions = {};
      state.messagesBySession = {};
      state.isLoading = false;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    /** Switch to the most recent session for a given agent (preserves data) */
    switchToAgent: (state, action: PayloadAction<string>) => {
      const agentId = action.payload;
      const agentSessions = Object.values(state.sessions)
        .filter((s) => s.agentId === agentId)
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
      state.activeSessionId =
        agentSessions.length > 0 ? agentSessions[0].id : null;
      state.isLoading = false;
      state.error = null;
    },
    /** Explicitly set the active session by ID */
    setActiveSession: (state, action: PayloadAction<string>) => {
      state.activeSessionId = action.payload;
    },
  },
  extraReducers: (builder) => {
    // --- Create Session ---
    builder.addCase(createSessionThunk.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createSessionThunk.fulfilled, (state, action) => {
      const { sessionId, agentId } = action.payload;
      state.isLoading = false;
      state.activeSessionId = sessionId;
      state.sessions[sessionId] = {
        id: sessionId,
        agentId,
        createdAt: new Date().toISOString(),
      };
      state.messagesBySession[sessionId] = [];
    });
    builder.addCase(createSessionThunk.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload ?? 'Failed to create session';
    });

    // --- Send Message ---
    builder.addCase(sendMessageThunk.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
      // Add user message + loading placeholder
      const messages = getActiveMessages(state);
      const userMessage: Message = {
        id: Date.now().toString(),
        content: action.meta.arg.message,
        role: 'user',
        timestamp: new Date().toISOString(),
      };
      const loadingMessage: Message = {
        id: `loading-${Date.now()}`,
        content: '',
        role: 'assistant',
        timestamp: new Date().toISOString(),
        isLoading: true,
      };
      messages.push(userMessage, loadingMessage);
      setActiveMessages(state, messages);
    });
    builder.addCase(sendMessageThunk.fulfilled, (state, action) => {
      state.isLoading = false;
      const messages = getActiveMessages(state).filter(
        (msg) => !msg.isLoading,
      );
      const assistantMessage: Message = {
        id: Date.now().toString(),
        content: action.payload.message,
        role: 'assistant',
        timestamp: new Date().toISOString(),
        trace_id: action.payload.trace_id,
      };
      messages.push(assistantMessage);
      setActiveMessages(state, messages);
    });
    builder.addCase(sendMessageThunk.rejected, (state, action) => {
      state.isLoading = false;
      const messages = getActiveMessages(state).filter(
        (msg) => !msg.isLoading,
      );
      setActiveMessages(state, messages);
      state.error = action.payload ?? 'Failed to send message';
    });

    // --- Submit Feedback ---
    builder.addCase(submitFeedbackThunk.rejected, (state, action) => {
      state.error = action.payload ?? 'Failed to submit feedback';
    });

    // --- Delete Session ---
    builder.addCase(deleteSessionThunk.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteSessionThunk.fulfilled, (state, action) => {
      const sessionId = action.payload;
      state.isLoading = false;
      delete state.sessions[sessionId];
      delete state.messagesBySession[sessionId];
      if (state.activeSessionId === sessionId) {
        state.activeSessionId = null;
      }
    });
    builder.addCase(deleteSessionThunk.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload ?? 'Failed to delete session';
    });
  },
});

export const { clearSessions, clearError, switchToAgent, setActiveSession } =
  sessionSlice.actions;
export default sessionSlice.reducer;
